"use strict";
/**
 * Generate Design — turn a workspace's brand, a headline and optionally a stored template into one
 * ready-to-edit design file.
 *
 * The contract lives in shared because the modal and the generator must agree on the aspect presets
 * and the mismatch threshold. Two copies of "what counts as square" is how a client mirror drifts
 * from the server rule it was written against.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DESIGN_JOB_STALE_AFTER_MS = exports.DESIGN_BACKGROUND_SOURCES = exports.DESIGN_RATIO_WARN_THRESHOLD = exports.DESIGN_MAX_EDGE = exports.DESIGN_MIN_EDGE = exports.DESIGN_ASPECT_PRESETS = exports.DESIGN_MODE_STATUS = exports.DESIGN_MODE_LABEL = exports.TEMPLATE_MODES = exports.DESIGN_TARGETS = void 0;
exports.ratioDrift = ratioDrift;
exports.closestPreset = closestPreset;
exports.generatesImagery = generatesImagery;
/**
 * Named by the FILE, not by one tool that opens it.
 *
 * `DesignTarget` is still 'figma' / 'canva' because that is what the server, the stored
 * BrandTemplateKind and every existing row already say — but an .svg is not Figma's format and a
 * .pptx is not Canva's, and naming the options after those two tools told people who use
 * Illustrator or PowerPoint that neither option was for them.
 */
exports.DESIGN_TARGETS = [
    {
        target: 'figma',
        label: 'SVG Template',
        format: 'Editable with Figma & Adobe Illustrator',
        note: 'Exports one .svg frame. Figma or Adobe Illustrator converts it to editable layers, with the headline as real text.',
    },
    {
        target: 'canva',
        label: 'PPTX Template',
        format: 'Editable with Canva & PowerPoint',
        note: 'Exports a single-slide .pptx for import into Canva or PowerPoint. PowerPoint is the only format Canva turns into editable text.',
    },
];
exports.TEMPLATE_MODES = [
    { value: 'as-is', label: 'Use as-is', hint: 'Swap the headline and image. Everything else untouched.' },
    {
        value: 'restyle',
        label: 'Restyle',
        hint: 'Keep every frame exactly; change colours and fonts to your brand.',
    },
    {
        value: 'redesign',
        label: 'Redesign',
        hint: 'Keep the template’s structure and palette, then re-compose the layout around it.',
    },
];
exports.DESIGN_MODE_LABEL = {
    freeform: 'freeform (brand assets)',
    'template-as-is': 'template · as-is',
    'template-restyled': 'template · restyled (Tier 1)',
    'template-redesigned': 'template · redesigned',
};
/** Status copy while a generation runs — one line per mode, so the wait says what is happening. */
exports.DESIGN_MODE_STATUS = {
    freeform: 'Composing design…',
    'template-as-is': 'Filling template…',
    'template-restyled': 'Restyling template…',
    'template-redesigned': 'Redesigning from template…',
};
/**
 * The offered sizes. Explicit width/height rather than a ratio string, because the fields the user
 * edits are pixels — deriving them from "4:5" would need a base nobody stated.
 */
exports.DESIGN_ASPECT_PRESETS = [
    { id: 'square', label: 'Square (1:1)', width: 1080, height: 1080 },
    { id: 'portrait', label: 'Portrait (4:5)', width: 1080, height: 1350 },
    { id: 'story', label: 'Vertical / Story (9:16)', width: 1080, height: 1920 },
    { id: 'landscape', label: 'Landscape (16:9)', width: 1920, height: 1080 },
    // Added after the design team standardised on five formats. The tightest canvas of the set: 630px of
    // height is the one most likely to expose a fit problem, which is why it is in the golden tests.
    { id: 'link', label: 'Link Preview (1.91:1)', width: 1200, height: 630 },
];
/** Bounds on the canvas. Below 64px nothing legible fits; above 8192 the SVG text maths overflows. */
exports.DESIGN_MIN_EDGE = 64;
exports.DESIGN_MAX_EDGE = 8192;
/**
 * How far a requested ratio may sit from a template's native ratio before it is worth saying so.
 *
 * A WARNING, never a refusal: the design still generates. Someone reusing a portrait template for a
 * story is making a deliberate choice, and the honest thing is to say the content may need nudging
 * after import — not to stop them.
 */
exports.DESIGN_RATIO_WARN_THRESHOLD = 0.25;
/** Relative difference between two aspect ratios, as a fraction of the smaller one. */
function ratioDrift(a, b) {
    if (!(a > 0) || !(b > 0))
        return 0;
    return Math.abs(a - b) / Math.min(a, b);
}
/** The preset closest to a given size, or null when nothing is within the warn threshold. */
function closestPreset(width, height) {
    if (!(width > 0) || !(height > 0))
        return null;
    const target = width / height;
    let best = null;
    let bestDrift = Number.POSITIVE_INFINITY;
    for (const preset of exports.DESIGN_ASPECT_PRESETS) {
        const drift = ratioDrift(target, preset.width / preset.height);
        if (drift < bestDrift) {
            best = preset;
            bestDrift = drift;
        }
    }
    // A 3:2 canvas is nearer 16:9 than anything else, but calling it "Landscape (16:9)" and quietly
    // meaning 3:2 would be a lie the width/height fields then contradict. Past the threshold it is
    // Custom, which is what it actually is.
    return bestDrift <= exports.DESIGN_RATIO_WARN_THRESHOLD ? best : null;
}
/**
 * Where a design's imagery comes from.
 *
 * FOUR options, and the order is deliberate. `flat` is first and default because it is free and
 * instant; the two generation options each fire a paid image call and decrement the workspace's daily
 * quota, so they should be a choice someone makes rather than one they land on by not reading the list.
 *
 * `abstract` and `image` stay separate rather than becoming one option with a prompt, because they
 * wrap that prompt differently and the difference is the point: abstract enforces non-representational
 * art no matter what is typed, so it cannot quietly become a worse version of the other.
 */
exports.DESIGN_BACKGROUND_SOURCES = [
    {
        value: 'flat',
        label: 'Flat brand colours',
        hint: 'No photo — just your palette. Free and instant.',
        costsQuota: false,
    },
    {
        value: 'abstract',
        label: 'Generate abstract background',
        hint: 'Texture and form in your brand colours. No subject.',
        costsQuota: true,
    },
    {
        value: 'image',
        label: 'Generate new image',
        hint: 'A picture of something you describe.',
        costsQuota: true,
    },
    {
        value: 'library',
        label: 'Media Library image',
        hint: 'Pick a photo you already have.',
        costsQuota: false,
    },
];
/**
 * Whether an image source fires a paid image call.
 *
 * Shared rather than duplicated because three places must agree: the service (whether to generate),
 * the controller (whether this press needs a job), and the modal (whether to show the generator and
 * prompt fields). Two of the four sources cost money; the answer is derived from the option table so
 * adding a fifth cannot leave one caller behind.
 */
function generatesImagery(source) {
    return exports.DESIGN_BACKGROUND_SOURCES.some((s) => s.value === source && s.costsQuota);
}
/**
 * How long a job may go without its row being touched before a reader calls it dead.
 *
 * This exists because every deploy restarts the container, which kills any job running in it. Without
 * a staleness rule those rows stay PENDING for ever and the modal spins against a worker that no longer
 * exists. The worker touches its row on every poll tick, so a live job is never quiet for this long.
 */
exports.DESIGN_JOB_STALE_AFTER_MS = 90_000;
//# sourceMappingURL=design-generation.js.map